// Live implementation of the PE serving-layer contract.
//
// INACTIVE until the backend serving layer lands. The mock layer is the default
// (see `index.ts`). When the backend ships, set VITE_SERVING_USE_MOCKS=false and
// this implementation takes over with NO changes to any screen.
//
// Each method maps 1:1 to a backend endpoint. The base URL comes from
// VITE_SERVING_API_BASE (frontend env only — no DB credentials live here).

import supabase from "../../utils/supabase";
import type {
  AssessmentItem,
  AssessmentsResponse,
  ChapterStandards,
  ContentKey,
  HitlQueueQuery,
  HitlQueueResponse,
  ItemType,
  Lang,
  MaterialDoc,
  Materials,
  ReleaseInput,
  ReleaseResult,
  ResponseMode,
  ScenesResponse,
  ServingApi,
  SubmitInput,
  SubmitResult,
  TeacherSettings,
  Tier,
} from "./types";

const BASE = (import.meta.env.VITE_SERVING_API_BASE as string | undefined) ?? "/api/v1/pe";

async function bearerHeader(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function getJson<T>(path: string, params: Record<string, string>): Promise<T> {
  const qs = new URLSearchParams(params).toString();
  const token = await bearerHeader();
  const res = await fetch(`${BASE}${path}?${qs}`, {
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    // Auth is the Bearer token above — NOT cookies. We intentionally do NOT set
    // credentials:"include": the serving backend's CORS allows the localhost dev
    // origin + the Authorization header but does NOT return
    // Access-Control-Allow-Credentials:true, so a credentialed cross-origin
    // request is blocked by the browser ("Failed to fetch") in dev. Omitting it
    // → fetch defaults to "same-origin": cookies still flow same-origin (prod),
    // and cross-origin dev requests are non-credentialed (which CORS permits).
  });
  if (!res.ok) throw new Error(`Serving API ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  const isForm = body instanceof FormData;
  const token = await bearerHeader();
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: isForm
      ? { ...authHeaders, ...extraHeaders }
      : { "Content-Type": "application/json", ...authHeaders, ...extraHeaders },
    // See getJson: Bearer-token auth, not cookies — no credentials:"include"
    // (would be blocked cross-origin in dev; harmless to omit on prod).
    body: isForm ? (body as FormData) : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Serving API ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

// Maps the backend's batch submit response to a single SubmitResult.
// Backend returns { results: [{item_id, status, queue_item_id, ...}], is_grade_released }.
function mapSubmitResult(itemId: string) {
  return (raw: any): SubmitResult => {
    const r = (raw.results as any[] ?? []).find((x: any) => x.item_id === itemId) ?? (raw.results ?? [])[0] ?? {};
    return {
      item_id: itemId,
      response_mode: r.response_mode ?? "audio",
      status: r.status ?? "queued_for_review",
      score: r.score ?? null,
      max_score: 1,
      is_correct: r.is_correct ?? null,
      feedback: r.feedback ?? null,
      queue_item_id: r.queue_item_id ?? null,
      is_grade_released: raw.is_grade_released ?? false,
    };
  };
}

// Strips a leading YAML frontmatter block (--- … ---) from a markdown string,
// returning the trimmed body. No-op when there is no frontmatter.
function stripFrontmatter(md: string): string {
  if (typeof md !== "string") return "";
  const m = md.match(/^\s*---\n[\s\S]*?\n---\n?/);
  return (m ? md.slice(m[0].length) : md).trim();
}

// Maps a raw /pe/materials row into the generic MaterialDoc shape (or null when
// the row is absent). content_json is passed through untouched — the FE renders
// it generically.
function toMaterialDoc(
  row:
    | { id: number; material_type: string; language: string; title: string; asset_url: string | null; content_json: any }
    | undefined,
  language: Lang,
): MaterialDoc | null {
  if (!row) return null;
  return {
    source_id: String(row.id),
    material_type: row.material_type,
    language: (row.language as Lang) ?? language,
    title: row.title,
    content_json: row.content_json,
    asset_url: row.asset_url ?? null,
  };
}

// Maps one raw serving-layer row into the contract AssessmentItem. /pe/assessments
// and /pe/quiz return the SAME enriched shape (answer withheld; mc choices,
// sentence_builder word_bank, and drag items/targets shuffled server-side), so
// both endpoints share exactly this one mapper.
function toAssessmentItem(it: any): AssessmentItem {
  return {
    source_id: it.source_item_id,
    item_id: it.addressable_id,
    chapter_id: String(it.chapter_id),
    scene_id: it.scene_id,
    tier: it.tier as Tier,
    language: it.language as Lang,
    type: it.item_type as ItemType,
    response_mode: (it.response_mode === "text" ? "auto" : it.response_mode) as ResponseMode,
    prompt: it.content_json?.prompt ?? "",
    // MC: server-provided shuffled choices (correct answer merged in, unlabeled).
    // Other types: fall back to distractors-only if present.
    choices: Array.isArray(it.choices)
      ? it.choices.map((text: string, i: number) => ({
          id: String.fromCharCode(97 + i),
          text,
          audio_url: it.choice_audio_urls?.[i] ?? null,
          image_url: it.choice_image_urls?.[i] ?? null,
        }))
      : Array.isArray(it.content_json?.distractors)
      ? it.content_json.distractors.map((text: string, i: number) => ({ id: String.fromCharCode(97 + i), text }))
      : undefined,
    word_bank: it.word_bank ?? it.content_json?.word_bank ?? undefined,
    audio_prompt_url: it.prompt_audio_url ?? it.content_json?.audio_url ?? null,
    drag_items: it.drag_items ?? undefined,
    drag_item_images: it.drag_item_image_urls ?? undefined,
    drag_targets: it.drag_targets ?? undefined,
  };
}

export const liveServingApi: ServingApi = {
  getScenes(key: ContentKey): Promise<ScenesResponse> {
    return getJson("/scenes", {
      chapter_id: key.chapterId,
      tier: key.tier,
      language: key.language,
    });
  },

  getMaterials({ chapterId, language }: { chapterId: string; language: Lang }): Promise<Materials> {
    // The backend returns a FLAT array — { chapter_id, language, count,
    // materials: [{ material_type, title, asset_url, content_json, ... }] } —
    // NOT the { observation_checklist, family_letter } contract the screens
    // consume. Reshape here (no backend edit): pick the two rows by
    // material_type and map content_json into the typed shapes. (Materials are
    // chapter+language scoped, never tier-scoped, so no tier param is sent.)
    return getJson<{
      chapter_id: number;
      language: string;
      count: number;
      materials: Array<{
        id: number;
        material_type: string;
        language: string;
        tier: string | null;
        title: string;
        asset_url: string | null;
        content_json: any;
      }>;
    }>("/materials", { chapter_id: chapterId, language }).then((raw): Materials => {
      const rows = raw.materials ?? [];
      const oc = rows.find((r) => r.material_type === "observation_checklist");
      const fl = rows.find((r) => r.material_type === "family_letter");
      return {
        chapter_id: String(raw.chapter_id),
        language: (raw.language as Lang) ?? language,
        observation_checklist: oc
          ? {
              source_id: String(oc.id),
              material_type: "observation_checklist",
              language: (oc.language as Lang) ?? language,
              title: oc.title,
              behaviors: Array.isArray(oc.content_json?.behaviors)
                ? oc.content_json.behaviors.map((b: any) => ({
                    text:
                      b?.behavior_text ??
                      (Array.isArray(b?.look_fors) ? b.look_fors[0] : "") ??
                      "",
                    scene_ref: typeof b?.scene_ref === "number" ? b.scene_ref : 0,
                    look_fors: Array.isArray(b?.look_fors) ? b.look_fors : undefined,
                    scaffolding_prompts: Array.isArray(b?.scaffolding_prompts)
                      ? b.scaffolding_prompts
                      : undefined,
                  }))
                : [],
              asset_url: oc.asset_url ?? null,
            }
          : null,
        family_letter: fl
          ? {
              source_id: String(fl.id),
              material_type: "family_letter",
              language: (fl.language as Lang) ?? language,
              title: fl.title,
              // Real content lives in content_json.body_md (markdown with a
              // leading YAML frontmatter block) — strip the frontmatter so the
              // parent-facing letter reads cleanly.
              body: stripFrontmatter(
                fl.content_json?.body_md ?? fl.content_json?.body ?? "",
              ),
              asset_url: fl.asset_url ?? null,
            }
          : null,
        // Doc-hub teacher materials — same /pe/materials response, surfaced
        // additively as raw structured docs (rendered generically by the FE).
        teacher_guide: toMaterialDoc(
          rows.find((r) => r.material_type === "teacher_guide"),
          language,
        ),
        quick_reference: toMaterialDoc(
          rows.find((r) => r.material_type === "quick_reference"),
          language,
        ),
        read_aloud_script: toMaterialDoc(
          rows.find((r) => r.material_type === "read_aloud_script"),
          language,
        ),
      };
    });
  },

  getChapterStandards(chapterId: string): Promise<ChapterStandards> {
    // Public endpoint; getJson still attaches the Bearer (harmless). Returns the
    // normalized { chapterId, frameworks:[{framework, standards:[...]}] } shape.
    return getJson<ChapterStandards>("/chapters/standards", { chapterId });
  },

  getAssessments(key: ContentKey): Promise<AssessmentsResponse> {
    // Send the launch tier: the serving layer is AUTHORITATIVE on tier scoping
    // and returns ONLY the items matching this tier (T1 launch -> T1 items). The
    // backend now REQUIRES tier (400 if absent) so an un-scoped, all-tiers set can
    // never be served. enforceTierComposition() below is retained as a
    // belt-and-suspenders no-op (open_response is T4-only, never in a T1/T2 serve).
    return getJson<{
      chapter_id: number;
      language: string;
      count: number;
      assessments: Array<{
        source_item_id: string;
        addressable_id: string;
        chapter_id: number;
        scene_id: string;
        tier: string;
        language: string;
        item_type: string;
        // Live API uses "text" for auto-scored, "audio" for HITL — contract uses "auto"/"audio"
        response_mode: string;
        // MC items: server now provides shuffled choices (correct merged with distractors)
        choices?: string[] | null;
        // drag_drop: shuffled items and targets (pairings withheld)
        drag_items?: string[] | null;
        drag_targets?: string[] | null;
        // sentence_builder: words parsed from prompt, shuffled (bracket portion stripped from prompt)
        word_bank?: string[] | null;
        // WS2 Task 1: ElevenLabs prompt narration (launch-language) + per-word timing.
        prompt_audio_url?: string | null;
        prompt_audio_timing?: Array<{ word: string; start: number; end: number }> | null;
        // WS2 Task 2: per-choice audio stream URLs, aligned with `choices` order (T1/T2 only).
        choice_audio_urls?: string[] | null;
        // T1 image items: per-choice / per-drag-item comic-still URLs, index-aligned.
        choice_image_urls?: (string | null)[] | null;
        drag_item_image_urls?: (string | null)[] | null;
        content_json: {
          prompt?: string;
          distractors?: string[] | null;
          word_bank?: string[] | null;
          audio_url?: string | null;
        } | null;
      }>;
    }>("/assessments", {
      chapter_id: key.chapterId,
      language: key.language,
      tier: key.tier,
    }).then((raw): AssessmentsResponse => {
      let items = raw.assessments.map(toAssessmentItem);
      // C1 — T1 formative filter (Phase 5 WS-D): T1 now serves audio_match PLUS
      // the #6 image-interaction items (3-option image-MC + tap-to-place image
      // drag), whose choices/drag items are comic-still .png hosted in R2. Mirror
      // the backend admit: keep audio_match always, and keep mc/drag_drop ONLY
      // when they carry image choices (a choice image_url, or a drag_item_images
      // entry). Text-only mc/drag and any typed item still never pass — so no
      // text-MC / open_response leaks to a pre-reading T1 student. T2–T4 unchanged.
      if (key.tier === "T1" && key.chapterId !== "practice") {
        items = items.filter(
          (it) =>
            it.type === "audio_match" ||
            (it.choices?.some((c) => !!c.image_url) ?? false) ||
            (it.drag_item_images?.some(Boolean) ?? false),
        );
      }
      return {
        chapter_id: String(raw.chapter_id),
        tier: key.tier,
        language: key.language as Lang,
        items,
      };
    });
  },

  getQuiz(key: ContentKey): Promise<AssessmentsResponse> {
    // Summative chapter quiz. /pe/quiz returns the SAME enriched item shape as
    // /pe/assessments (under an `items` key), so it reuses the shared mapper.
    // The correct answer is withheld; scoring happens server-side in /pe/submit.
    // T3/T4 only (the backend rejects T1/T2).
    return getJson<{
      chapter_id: number;
      tier: string;
      language: string;
      count: number;
      items: any[];
    }>("/quiz", {
      chapter_id: key.chapterId,
      language: key.language,
      tier: key.tier,
    }).then((raw): AssessmentsResponse => ({
      chapter_id: String(raw.chapter_id),
      tier: key.tier,
      language: key.language as Lang,
      items: (raw.items ?? []).map(toAssessmentItem),
    }));
  },

  submitItem(input: SubmitInput): Promise<SubmitResult> {
    // Audio blob — real mic recording (WS2 Task 3). Multipart to /submit-audio:
    // the backend stores the recording in R2 and routes the item to HITL with
    // the recording key (teacher hears the actual student).
    if (input.response_mode === "audio" && input.audio) {
      const form = new FormData();
      form.append("item_id", input.item_id);
      if (input.student_assignment_id) form.append("student_assignment_id", input.student_assignment_id);
      form.append("audio", input.audio, `${input.source_id}.webm`);
      return postJson<any>("/submit-audio", form).then(mapSubmitResult(input.item_id));
    }

    // mc: response = the selected choice text (input.text populated by
    //   FormativeAssessment from item.choices).
    // sentence_builder: response = input.text (the assembled sentence).
    // drag_drop: response = input.text — a JSON array of the placed values in
    //   chosen order, produced by the tap-to-place DragDropRenderer.
    // open_response: response = input.text.
    const response = input.text ?? null;
    return postJson<any>("/submit", {
      student_assignment_id: input.student_assignment_id,
      answers: [{ item_id: input.item_id, response }],
    }).then(mapSubmitResult(input.item_id));
  },

  releaseScore(input: ReleaseInput): Promise<ReleaseResult> {
    // Prefer the caller-supplied ltik (from the queue's own LTI launch) so the
    // token is fresh. Fall back to localStorage only when no ltik was passed.
    const ltik = input.ltik ?? (typeof localStorage !== "undefined" ? localStorage.getItem("ltiToken") : null);
    const { ltik: _omit, ...body } = input;
    const extraHeaders: Record<string, string> = ltik ? { "x-lti-token": ltik } : {};
    return postJson("/release", body, extraHeaders);
  },

  getHitlQueue(query: HitlQueueQuery): Promise<HitlQueueResponse> {
    const params: Record<string, string> = {};
    if (query.teacherId) params.teacher_id = query.teacherId;
    if (query.classId) params.class_id = query.classId;
    if (query.chapterId) params.chapter_id = query.chapterId;
    return getJson("/hitl-queue", params);
  },

  // WS2 Rider: read-only per-teacher PE config (ai_auto_release is plumbing only).
  getTeacherSettings(): Promise<TeacherSettings> {
    return getJson("/teacher-settings", {});
  },
};
